import { IDownstreamTargetStatus, IEnvironmentRepairNeeded, ISourceDriftLastRun, IStatusTableModel, ISyncResetRequest, IWorkerCreateRequest, IWorkerDisplay } from './sync-monitoring.models';

const SYNC_ERROR_MAX_LENGTH = 200;
const HEARTBEAT_STALE_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Self-link of the only supported worker-filter type (core-one FilterTypeEnum.Tag). Filters are sent as `{ type: { self: WORKER_FILTER_TAG_TYPE_SELF }, ... }` — the framework's SelfEnum / DiscriminatorConverter requires the `type` discriminator to be this `{ self }` object (a plain string is rejected). Confirmed by a
 * live GET /workers response carrying exactly this shape.
 */
export const WORKER_FILTER_TAG_TYPE_SELF = '/worker-filter-types/tag';

/** Default filter name. core-one routes sync jobs to workers by matching a filter whose Name is "Type" against the job's "Type" tag (SyncQueueService), so new filter rows default to this. */
export const WORKER_FILTER_NAME_TYPE = 'Type';

/** The job "Type" tag values core-one emits (SyncQueueService._JobType constants). Offered as autocomplete suggestions for a filter's value — but the field stays free-text because the matcher also accepts comma-separated lists ("TableSync,TableStatus") and `*` wildcards ("Table_"). */
export const WORKER_FILTER_JOB_TYPE_VALUES = ['TableSync', 'TableStatus', 'TableCollation'];

// Display_Status_Precedence (operational urgency, lower number = more urgent).
const STATUS_SORT_PRIORITY: Record<string, number> = {
	failure: 0,
	ignored: 1,
	stale: 2,
	synchronizing: 3,
	pending: 4,
	synchronized: 5,
	unknown: 6,
};

// Status slugs the backend reports on a table's status self-link that map directly to a label.
const KNOWN_TABLE_STATUS_SLUGS = new Set(['synchronizing', 'pending', 'synchronized']);

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/** Extracts the table name from a status table self link. Example: "/one/environments/{id}/tables/T_Article" -> "T_Article" */
export function extractTableName(self: string): string {
	if (!self) {
		return '';
	}
	const afterTables = self.split('/tables/')[1];
	const candidate = afterTables ?? self;
	return candidate.split('/').filter(Boolean).pop() ?? '';
}

/** Extracts the worker id (trailing GUID) from a worker self link. Example: "/one/environments/{envId}/workers/d7fb0b59-846e-493e-9676-dc3a8b167266" -> "d7fb0b59-846e-493e-9676-dc3a8b167266" */
export function extractWorkerId(self: string | null | undefined): string {
	if (!self) {
		return '';
	}
	const afterWorkers = self.split('/workers/')[1];
	const candidate = afterWorkers ?? self;
	return candidate.split('/').filter(Boolean).pop() ?? '';
}

/** Extracts the status label (slug) from a status self link, lowercased. Example: "/one/environment-table-synchronization-statuses/synchronized" -> "synchronized" */
export function extractStatusLabel(status: { self: string }): string {
	if (!status?.self) {
		return '';
	}
	return (status.self.split('/').filter(Boolean).pop() ?? '').toLowerCase();
}

/** Localized unit labels for {@link formatStaleDuration}'s singular/plural output. */
export interface IStaleDurationUnitLabels {
	day: string;
	days: string;
	hour: string;
	hours: string;
	minute: string;
	minutes: string;
	second: string;
	seconds: string;
}

// Defaults preserve this function's original (English) output exactly, so callers that don't
// need translation — including the existing property tests — are unaffected.
const DEFAULT_STALE_DURATION_UNIT_LABELS: IStaleDurationUnitLabels = {
	day: 'day',
	days: 'days',
	hour: 'hour',
	hours: 'hours',
	minute: 'minute',
	minutes: 'minutes',
	second: 'second',
	seconds: 'seconds',
};

/** Converts a millisecond duration to the largest whole unit with a unit label. Examples: 90000 -> "1 minute", 3700000 -> "1 hour", 0 -> "0 seconds" */
export function formatStaleDuration(ms: number, unitLabels: IStaleDurationUnitLabels = DEFAULT_STALE_DURATION_UNIT_LABELS): string {
	const safe = Number.isFinite(ms) && ms > 0 ? Math.floor(ms) : 0;
	const units: [number, keyof IStaleDurationUnitLabels, keyof IStaleDurationUnitLabels][] = [
		[MS_PER_DAY, 'day', 'days'],
		[MS_PER_HOUR, 'hour', 'hours'],
		[MS_PER_MINUTE, 'minute', 'minutes'],
		[MS_PER_SECOND, 'second', 'seconds'],
	];
	for (const [unitMs, singular, plural] of units) {
		if (safe >= unitMs) {
			const value = Math.floor(safe / unitMs);
			return `${value} ${value === 1 ? unitLabels[singular] : unitLabels[plural]}`;
		}
	}
	return `0 ${unitLabels.seconds}`;
}

/** Converts a delay in seconds to a compact human-readable duration. Examples: 3661 -> "1h 1m 1s", 90 -> "1m 30s", 0 -> "0s" */
export function formatDelay(seconds: number): string {
	const safe = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
	const hours = Math.floor(safe / 3600);
	const minutes = Math.floor((safe % 3600) / 60);
	const secs = safe % 60;
	const parts: string[] = [];
	if (hours > 0) {
		parts.push(`${hours}h`);
	}
	if (minutes > 0) {
		parts.push(`${minutes}m`);
	}
	if (secs > 0 || parts.length === 0) {
		parts.push(`${secs}s`);
	}
	return parts.join(' ');
}

/**
 * Derives the displayed status for a table following Display_Status_Precedence: failure > ignored > stale > (status slug: synchronizing | pending | synchronized) > unknown
 *
 * Note: "disabled" is an environment-level concept, not a per-table status, so a per-table status slug outside the known set falls through to "unknown".
 */
export function deriveTableStatus(table: IStatusTableModel): string {
	const slug = extractStatusLabel(table.status);

	// An explicit "unknown" status means the table's status is currently being (re)checked — it is
	// pending, NOT an error. The API pairs this state with a placeholder `failure: "Status not found"`;
	// that is not a real failure, so an explicit unknown status takes precedence over the failure
	// check below (which would otherwise paint every rechecking table red).
	if (slug === 'unknown') {
		return 'unknown';
	}
	if (table.failure != null && table.failure.trim() !== '') {
		return 'failure';
	}
	if (table.synchronization?.ignore === true) {
		return 'ignored';
	}
	if (table.itemSync?.stale === true) {
		return 'stale';
	}
	return KNOWN_TABLE_STATUS_SLUGS.has(slug) ? slug : 'unknown';
}

/** Whether a downstream target is synchronized: its `synchronized` flag, or its status when the API leaves the flag out. */
export function isTargetSynchronized(target: IDownstreamTargetStatus): boolean {
	return target.synchronized ?? target.status?.toLowerCase() === 'synchronized';
}

/** Whether a target reports its progress as a snapshot of the target table (row count and max row version) instead of queued and completed item counts. core-engine does: recording its snapshot clears the counts, so showing them would read as "0 completed". */
export function hasTargetRowSnapshot(target: IDownstreamTargetStatus): boolean {
	return target.targetObservedRowCount != null && target.queuedItemCount == null && target.completedItemCount == null;
}

/** The Source drift badge: rows missing in Core One for a positive difference, extra items in Core One for a negative one, and none without a difference. */
export function sourceDriftBadge(difference: number | null | undefined): { direction: 'missing' | 'extra'; count: number } | null {
	if (!difference) {
		return null;
	}
	return { direction: difference > 0 ? 'missing' : 'extra', count: Math.abs(difference) };
}

// The range-check run states and failure reasons core-one sends, each with its own text.
const SOURCE_DRIFT_KEYS = {
	states: new Set(['running', 'completed', 'failed']),
	reasons: new Set(['jobFailed', 'jobExpired', 'tooManyRanges', 'generationChanged', 'versionChanged', 'superseded', 'unreadableCallback', 'resendFailed', 'leafNotCovered']),
};

/** The translation key for a range-check run's state or failure reason. A value core-one adds later gets the generic text, which shows it raw as `{{value}}`. */
export function sourceDriftKey(group: keyof typeof SOURCE_DRIFT_KEYS, value: string | null | undefined): string {
	return `administration.sync-monitoring.source-drift.${group}.${value != null && SOURCE_DRIFT_KEYS[group].has(value) ? value : 'unknown'}`;
}

/** Whether a finished range check found rows and repaired none: nothing was re-sent and nothing expired. */
export function isFoundNotRepaired(run: ISourceDriftLastRun): boolean {
	const found = (run.missingFound ?? 0) + (run.extraFound ?? 0);
	return run.state !== 'running' && found > 0 && !run.resent && !run.expired;
}

/** Maps a status label to its operational-urgency sort priority (lower = more urgent): failure=0, ignored=1, stale=2, synchronizing=3, pending=4, synchronized=5, unknown=6. Unrecognized labels sort as "unknown". */
export function getStatusSortPriority(status: string): number {
	return STATUS_SORT_PRIORITY[status] ?? STATUS_SORT_PRIORITY['unknown'];
}

/** Maps a worker to its sort priority (lower = shown first): stale=0, heartbeat-unavailable=1, never-reported=2, healthy=3. */
export function getWorkerSortPriority(worker: IWorkerDisplay): number {
	if (worker.hasHeartbeat && worker.isStale) {
		return 0;
	}
	if (worker.heartbeatUnavailable) {
		return 1;
	}
	if (!worker.hasHeartbeat) {
		return 2;
	}
	return 3;
}

/** Truncates a message to a maximum visible length (INCLUDING the ellipsis), appending an ellipsis when truncated. Strings at or under the limit are returned unchanged (Property 3). */
export function truncateSyncError(value: string | null, maxLength: number = SYNC_ERROR_MAX_LENGTH): string {
	if (!value) {
		return '';
	}
	return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
}

/** Builds the synchronization-reset request body: an empty filter resets all tables, a single table self-link resets just that table. rowVersion passes through unchanged (Property 5). */
export function buildSyncResetRequest(tableSelf: string | readonly string[] | null | undefined, rowVersion: number): ISyncResetRequest {
	const selves = typeof tableSelf === 'string' ? [tableSelf] : (tableSelf ?? []);
	return {
		filter: selves.filter((self) => self.length > 0).map((self) => ({ self })),
		rowVersion,
	};
}

/** Core-one's `repairNeeded.recommendedAction` values. */
export const REPAIR_ACTION = 'Repair';
export const RESET_TABLES_ACTION = 'ResetTables';

/** Whether core-one recommends applying the repair rather than resetting the tables. Anything but an explicit 'Repair' recommends the reset, which is safe in any state. */
export function isRepairRecommended(repairNeeded: IEnvironmentRepairNeeded | null | undefined): boolean {
	return repairNeeded?.recommendedAction === REPAIR_ACTION;
}

/** The reset targets for a repair-needed record: each named table's self under the environment (the same `/one/environments/:id/tables/:name` shape the status rows carry). Blank names are dropped, so an empty result means "nothing to reset", never "all tables". */
export function buildRepairNeededTables(environmentSelf: string, tables: readonly string[] | null | undefined): { name: string; self: string }[] {
	const base = environmentSelf.replace(/\/+$/, '');
	return (tables ?? []).filter((name) => name.trim().length > 0).map((name) => ({ name, self: `${base}/tables/${name}` }));
}

/**
 * Builds the create-worker request body. Trims the name and each filter's name/value, drops any filter row that is entirely empty (both name and value blank), and stamps every remaining filter with the `tag` type reference — the only type core-one supports. A row with only one of name/value filled is kept as-is so
 * server-side validation (both required) surfaces it rather than silently dropping a half-entered filter.
 */
export function buildWorkerCreateRequest(name: string, filters: { name: string; value: string }[]): IWorkerCreateRequest {
	return {
		name: name.trim(),
		filters: filters
			.map((filter) => ({ name: filter.name.trim(), value: filter.value.trim() }))
			.filter((filter) => filter.name !== '' || filter.value !== '')
			.map((filter) => ({
				type: { self: WORKER_FILTER_TAG_TYPE_SELF },
				name: filter.name,
				value: filter.value,
			})),
	};
}

/** Determines worker heartbeat staleness: stale iff the heartbeat is strictly more than 5 minutes (300000ms) old. The boundary itself (exactly 300000ms) is NOT stale (Property 4). */
export function isHeartbeatStale(heartbeatAtMs: number, nowMs: number): boolean {
	return nowMs - heartbeatAtMs > HEARTBEAT_STALE_THRESHOLD_MS;
}

/** Pretty-prints a worker's serialized `oneConfiguration` (a JSON string) for display. Falls back to the raw string when it isn't valid JSON, so the value is never hidden by a parse failure. */
export function formatWorkerConfiguration(raw: string | null | undefined): string {
	if (!raw) {
		return '';
	}
	try {
		return JSON.stringify(JSON.parse(raw), null, 2);
	} catch {
		return raw;
	}
}

/** One Monitor's deep-link query parameters, set by the cross-account overview's links. */
export interface IOneMonitorLinkParams {
	accountId?: string | null;
	environmentId?: string | null;
	table?: string | null;
	kind?: string | null;
}

/** What One Monitor applies from a deep link at one point of its loading. */
export interface IOneMonitorLinkStep {
	/** A notice to show: a key under `administration.sync-monitoring.link`. */
	notice?: 'unknown-environment' | 'unknown-table';
	/** The link's account, when it is not the selected one: One Monitor switches to it and opens the link again there. */
	switchToAccount?: string;
	/** The linked environment to select. */
	environmentId?: string;
	/** The linked table's self link, to pass to `navigateToTable` with the link's kind. */
	tableSelf?: string;
	/** True once the link is handled and its parameters can leave the URL. */
	done: boolean;
}

/**
 * Decides what One Monitor applies from a deep link, given what has loaded so far: `environmentIds` and the linked environment's `tableSelves` are null until loaded. A link for another account than the selected one asks for a switch to that account; an unknown environment or table keeps the default with a notice.
 * Returns null when there is no link.
 */
export function decideOneMonitorLink(params: IOneMonitorLinkParams, selectedAccountId: string | null, environmentIds: readonly string[] | null, tableSelves: readonly string[] | null): IOneMonitorLinkStep | null {
	const { accountId, environmentId, table, kind } = params;
	if (!accountId && !environmentId && !table && !kind) {
		return null;
	}
	if (accountId && accountId !== selectedAccountId) {
		return { switchToAccount: accountId, done: false };
	}
	if (!environmentId) {
		return { done: true };
	}
	if (!environmentIds) {
		return { done: false };
	}
	if (!environmentIds.includes(environmentId)) {
		return { notice: 'unknown-environment', done: true };
	}
	if (!table) {
		return { environmentId, done: true };
	}
	if (!tableSelves) {
		return { environmentId, done: false };
	}
	const tableSelf = tableSelves.find((self) => extractTableName(self) === table);
	return tableSelf ? { environmentId, tableSelf, done: true } : { environmentId, notice: 'unknown-table', done: true };
}
